import React from "react";

export type CrewmateColor =
  | "red"
  | "green"
  | "yellow"
  | "blue"
  | "pink"
  | "orange"
  | "purple"
  | "cyan";

export type CrewmateSize = "small" | "medium" | "large";

interface CrewmateProps {
  color: CrewmateColor;
  size?: CrewmateSize;
  className?: string;
  style?: React.CSSProperties;
}

const colorMap = {
  red: "#C51111",
  green: "#11802D",
  yellow: "#F3F318",
  blue: "#132ED1",
  pink: "#ED54BA",
  orange: "#F07D0D",
  purple: "#8E24AA",
  cyan: "#00ACC1",
} as const;

const sizeMap = {
  small: { width: 80, height: 100 },
  medium: { width: 120, height: 150 },
  large: { width: 160, height: 200 },
} as const;

export const Crewmate: React.FC<CrewmateProps> = ({
  color,
  size = "medium",
  className = "",
  style = {},
}) => {
  const colorValue = colorMap[color];
  const { width, height } = sizeMap[size];

  return (
    <div
      className={`crewmate-container ${className}`}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        ...style,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="108 140 56 42"
        width="100%"
        height="100%"
        style={{ shapeRendering: "geometricPrecision" }}
      >
        {/* Main body outline */}
        <path
          fill="black"
          d="M132.4679 142.0571c-2.6616-.3718-4.531.0644-5.996 1.5398-2.1498 2.165-1.6178 3.6435-2.6603 6.0998.3514 5.611 3.3461 8.9031 8.7571 8.4406 6.7524-.577 8.2116-6.8451 7.2326-8.9616.0202-.8398.02-1.2944-.3279-2.1528-.1464-.361-.1868-.4523-.4915-.8879-.7409-1.0586-.0498-.1565-.7249-.8378-.7615-1.4698-3.3587-2.9581-5.7891-3.2401zm-15.0969 18.4981c-.4955 3.7782.001 12.0137 1.3151 15.1972.9797 2.3736 5.1688 1.7305 5.9033-.5368.189-.5838.3338-3.4152.3644-4.2143.063-1.6404-.0165-1.2115-.9708-2.3751-.406-.495-.2108-.0676-.4736-.689 1.8025-.0726 4.004.7462 5.8361.6679 2.0479-.0873 2.9558-1.1783 4.2556-1.7437-.892 1.8597-1.523 1.5845-1.934 3.505-.2715 1.2682-.2387 2.7762-.3318 4.0518 3.6948 1.0773 4.3698-.5279 4.9215-4.0273.3564-2.261.8172-5.3957.3602-7.2551l-.0567-3.9898c-3.0565.4916-5.1249 1.6423-9.0073-.0436-5.793-2.5157-6.8388-9.763-3.6942-14.9903 1.802-2.9956 3.3125-3.0142 6.2535-4.1193-5.3101-1.4757-9.8368.2831-11.5114 5.2074-.8475 2.4923-1.4756 12.9016-1.2299 15.355zm-5.4622-6.8157c-.7315 3.1237-.9236 6.431-.9397 9.4665-.0189 3.5468-.0694 5.8156 4.3945 5.4726.3746-1.9026.0524-5.1602.0903-7.3613.0428-2.479.5099-5.402.2986-7.6007l.2446-3.0419c-2.5765-.1738-3.3351.5109-4.0883 3.0648zm3.824-5.2132c1.041-4.4369 2.1316-7.3677 4.8581-9.2072 2.3825-1.6074 6.5198-2.0732 9.8706-1.2345 1.2108.3032 7.5747 4.2114 8.523 5.1258 3.0868 2.9766 3.3513 6.2168 2.3248 10.5922-.5251 2.238-2.7333 3.9271-2.8455 4.4054-.578 2.4636 2.5821 20.5354-6.8361 18.3256-2.6632-.6248-2.4079-3.3891-1.8824-5.8769-1.3494-.2004-1.1879.0836-2.8281-.3013-.4612 2.1833.111 4.8822-.7722 6.3187-1.717 2.7927-4.5165 2.5865-6.9794 2.0086-2.7433-.6436-3.542-6.8251-3.5401-8.0777-7.1088-.0112-6.7786-3.6378-6.4884-10.0655.3945-8.7382.9431-10.7674 6.5957-12.0132z"
        />

        {/* Main body fill */}
        <path
          fill={colorValue}
          d="M136.617 163.1358c-.5349 1.8352-2.7094 2.2082-4.3632 2.6047-5.2789 1.2658-9.2584-.108-13.2521-3.388l-1.4593-1.5592c-.1247-.0602-.1103-.1617-.1714-.2381-.2457-2.4534.3824-12.8627 1.2299-15.355 1.6746-4.9243 6.2013-6.6831 11.5114-5.2074-2.941 1.1051-4.4515 1.1237-6.2535 4.1193-3.1446 5.2273-2.0988 12.4746 3.6942 14.9903 3.8824 1.6859 5.9508.5352 9.0073.0436l.0567 3.9898zm0 0c.457 1.8594-.0038 4.9941-.3602 7.2551-.5517 3.4994-1.2267 5.1046-4.9215 4.0273.0931-1.2756.0603-2.7836.3318-4.0518.411-1.9205 1.042-1.6453 1.934-3.505-1.2998.5654-2.2077 1.6564-4.2556 1.7437-1.8321.0783-4.0336-.7405-5.8361-.6679.2628.6214.0676.194.4736.689.9543 1.1636 1.0338.7347.9708 2.3751-.0306.7991-.1754 3.6305-.3644 4.2143-.7345 2.2673-4.9236 2.9104-5.9033.5368-1.3141-3.1835-1.8106-11.419-1.3151-15.1972.0611.0764.0467.1779.1714.2381l1.4593 1.5592c3.9937 3.28 7.9732 4.6538 13.2521 3.388 1.6538-.3965 3.8283-.7695 4.3632-2.6047zm-20.8645-9.4192c.2113 2.1987-.2558 5.1217-.2986 7.6007-.0379 2.2011.2843 5.4587-.0903 7.3613-4.4639.343-4.4134-1.9258-4.3945-5.4726.0161-3.0355.2082-6.3428.9397-9.4665 1.2233.7858 2.8604 2.21 3.8437-.0229zm0 0c-.9833 2.2329-2.6204.8087-3.8437.0229.7532-2.5539 1.5118-3.2386 4.0883-3.0648l-.2446 3.0419z"
        />

        {/* Visor glass */}
        <path
          fill="#95C9DC"
          d="M132.4679 142.0571c-.0883.0524-.2123.0995-.2795.1295l-1.7237.7833c-.5921.3879-.9383.7847-1.1297 1.5223-.8645 3.3309 4.1748 3.537 6.192 3.2753 1.7063-.2214 2.0395-.6944 2.73-2.4703.6751.6813-.016-.2208.7249.8378.3047.4356.3451.5269.4915.8879.3479.8584.3481 1.313.3279 2.1528-.6322 3.9828-4.1068 6.0123-7.2685 6.1107-2.0188.0629-3.8155-.3506-5.4302-1.4806-2.1374-1.496-1.8394-2.5224-3.291-4.1091 1.0425-2.4563.5105-3.9348 2.6603-6.0998 1.465-1.4754 3.3344-1.9116 5.996-1.5398z"
        />

        {/* Visor frame */}
        <path
          fill="#465A61"
          d="M139.8013 149.1757c.979 2.1165-.4802 8.3846-7.2326 8.9616-5.411.4625-8.4057-2.8296-8.7571-8.4406 1.4516 1.5867 1.1536 2.6131 3.291 4.1091 1.6147 1.13 3.4114 1.5435 5.4302 1.4806 3.1617-.0984 6.6363-2.1279 7.2685-6.1107z"
        />

        {/* Visor highlight */}
        <path
          fill="#FEFDFE"
          d="M132.4679 142.0571c2.4304.282 5.0276 1.7703 5.7891 3.2401-.6905 1.7759-1.0237 2.2489-2.73 2.4703-2.0172.2617-7.0565.0556-6.192-3.2753.1914-.7376.5376-1.1344 1.1297-1.5223l1.7237-.7833c.0672-.03.1912-.0771.2795-.1295z"
        />

        {/* Body shadows */}
        <path
          fill="black"
          fillOpacity="0.21"
          d="M136.617 163.1358c.457 1.8594-.0038 4.9941-.3602 7.2551-.5517 3.4994-1.2267 5.1046-4.9215 4.0273.0931-1.2756.0603-2.7836.3318-4.0518.411-1.9205 1.042-1.6453 1.934-3.505-1.2998.5654-2.2077 1.6564-4.2556 1.7437-1.8321.0783-4.0336-.7405-5.8361-.6679.2628.6214.0676.194.4736.689.9543 1.1636 1.0338.7347.9708 2.3751-.0306.7991-.1754 3.6305-.3644 4.2143-.7345 2.2673-4.9236 2.9104-5.9033.5368-1.3141-3.1835-1.8106-11.419-1.3151-15.1972.0611.0764.0467.1779.1714.2381l1.4593 1.5592c3.9937 3.28 7.9732 4.6538 13.2521 3.388 1.6538-.3965 3.8283-.7695 4.3632-2.6047zM115.7525 153.7166c.2113 2.1987-.2558 5.1217-.2986 7.6007-.0379 2.2011.2843 5.4587-.0903 7.3613-4.4639.343-4.4134-1.9258-4.3945-5.4726.0161-3.0355.2082-6.3428.9397-9.4665 1.2233.7858 2.8604 2.21 3.8437-.0229z"
        />
      </svg>
    </div>
  );
};
